import * as babylon from "@babylonjs/core";

import * as schema from "@/shared/state/visualization/schema/v1/three-d";

/**
 * One sample's worth of channel values, as the rotation they describe.
 *
 * Every mode collapses to a quaternion here, once, at load time — so the
 * per-frame path only ever slerps, and the hierarchy composes one kind of thing.
 *
 * Degenerate input yields no rotation rather than `NaN`: a zero axis names no
 * direction, and an all-zero quaternion is what a channel with no data at this
 * time produces. Both would otherwise poison every descendant's transform, since
 * a `NaN` propagates through the whole composition.
 *
 * @param spec the rotation being sampled, which fixes how `values` are read
 * @param values `[angle]`, `[roll, pitch, yaw]`, or `[x, y, z, w]` — a bare
 *   number is accepted for the single-channel case
 */
export function quaternionFor(
  spec: schema.Rotation,
  values: number[] | number,
): babylon.Quaternion {
  const channels = typeof values === "number" ? [values] : values;

  switch (spec.type) {
    case "axis_angle": {
      const axis = new babylon.Vector3(
        spec.axis[0],
        spec.axis[1],
        spec.axis[2],
      );
      if (axis.lengthSquared() === 0) {
        return babylon.Quaternion.Identity();
      }
      return babylon.Quaternion.RotationAxis(
        axis.normalize(),
        channels[0] ?? 0,
      );
    }
    case "euler": {
      const [roll = 0, pitch = 0, yaw = 0] = channels;
      if (spec.convention === undefined && spec.order === undefined) {
        // Every rotation saved before conventions: roll about the scene's X,
        // pitch about its Y, yaw about its Z, composed the way Babylon's own
        // (yaw about Y, pitch about X, roll about Z) argument order does.
        return babylon.Quaternion.RotationYawPitchRoll(pitch, roll, yaw);
      }
      const convention = spec.convention ?? "scene";
      return toSceneRotation(
        eulerQuaternion(spec.order ?? DEFAULT_EULER_ORDER[convention], [
          roll,
          pitch,
          yaw,
        ]),
        convention,
      );
    }
    case "quaternion": {
      const [x = 0, y = 0, z = 0, w = 0] = channels;
      const quaternion = new babylon.Quaternion(x, y, z, w);
      if (quaternion.lengthSquared() === 0) {
        return babylon.Quaternion.Identity();
      }
      const normalized = quaternion.normalize();
      return spec.convention
        ? toSceneRotation(normalized, spec.convention)
        : normalized;
    }
  }
}

type EulerOrder = NonNullable<schema.EulerRotation["order"]>;

/**
 * The order a convention's euler angles compose in when the spec names none.
 *
 * Every source convention uses fixed-axis XYZ, `R = Rz·Ry·Rx`: URDF's
 * `<origin rpy>`, REP-103's roll, pitch and yaw, and PX4's ZYX Euler angles are
 * all this one rotation. `scene` keeps the composition saved rotations were
 * authored with.
 */
const DEFAULT_EULER_ORDER: Record<schema.ConventionId, EulerOrder> = {
  flu: "XYZ",
  enu: "XYZ",
  ned: "XYZ",
  frd: "XYZ",
  rdf: "XYZ",
  scene: "ZXY",
};

const AXIS_VECTOR = {
  X: babylon.Axis.X,
  Y: babylon.Axis.Y,
  Z: babylon.Axis.Z,
} as const;

/**
 * Roll about x, pitch about y and yaw about z, in the source frame's own axes,
 * as turns about fixed axes applied in `order`, first letter first.
 */
function eulerQuaternion(
  order: EulerOrder,
  [roll, pitch, yaw]: [number, number, number],
): babylon.Quaternion {
  const angle = { X: roll, Y: pitch, Z: yaw };
  return (order.split("") as (keyof typeof AXIS_VECTOR)[]).reduce(
    (composed, axis) =>
      babylon.Quaternion.RotationAxis(AXIS_VECTOR[axis], angle[axis]).multiply(
        composed,
      ),
    babylon.Quaternion.Identity(),
  );
}

/**
 * A rotation stated in a convention's source frame, carried into the scene.
 *
 * Changing frames by a mapping `M` turns a rotation `R` into `M·R·Mᵀ`, which
 * for a quaternion `(v, w)` is `(det(M)·M·v, w)`: the axis moves with the
 * frame, and a reflection reverses the sense of turn. When the rotated body's
 * axes follow a different convention from the world's (a forward/left/up robot
 * in an East/North/Up world), the constant turn between the two follows.
 */
function toSceneRotation(
  source: babylon.Quaternion,
  id: schema.ConventionId,
): babylon.Quaternion {
  if (id === "scene") {
    return source;
  }
  const world = schema.CONVENTIONS[id];
  const sign = schema.determinantOf(world);
  const [x, y, z] = schema.toScene(world, [source.x, source.y, source.z]);
  const conjugated = new babylon.Quaternion(
    sign * x,
    sign * y,
    sign * z,
    source.w,
  );
  const turn = bodyTurn(id);
  return turn ? conjugated.multiply(turn) : conjugated;
}

const bodyTurns = new Map<schema.ConventionId, babylon.Quaternion | null>();

/**
 * The constant scene rotation `M_world·M_bodyᵀ` that takes a body laid out in
 * the scene by its own convention onto the world's, or null when the two
 * conventions are the same.
 */
function bodyTurn(id: schema.ConventionId): babylon.Quaternion | null {
  const cached = bodyTurns.get(id);
  if (cached !== undefined) {
    return cached;
  }
  const bodyId = schema.bodyConventionFor(id);
  let turn: babylon.Quaternion | null = null;
  if (bodyId !== id) {
    const world = schema.CONVENTIONS[id];
    const body = schema.CONVENTIONS[bodyId];
    // Column j is where scene axis j of the body's layout lands in the world's:
    // back through the body's mapping to a source axis, then out through the
    // world's.
    const columns = [body.right, body.up, body.forward].map((slot) => {
      const source: [number, number, number] = [0, 0, 0];
      source[{ x: 0, y: 1, z: 2 }[slot.field]] = slot.scale;
      return schema.toScene(world, source);
    });
    const isIdentity = columns.every((column, j) =>
      column.every((value, i) => value === (i === j ? 1 : 0)),
    );
    // `ned` and `frd` name the same axes differently: no turn between them.
    turn = isIdentity ? null : quaternionFromColumns(columns);
  }
  bodyTurns.set(id, turn);
  return turn;
}

/**
 * The quaternion of the proper rotation whose matrix has these columns, by
 * Shepperd's method. Babylon applies a quaternion with the same algebra as a
 * column-vector rotation matrix, so this needs no handedness adjustment.
 */
function quaternionFromColumns(columns: number[][]): babylon.Quaternion {
  const m = (row: number, column: number) => columns[column][row];
  const trace = m(0, 0) + m(1, 1) + m(2, 2);
  if (trace > 0) {
    const s = Math.sqrt(trace + 1) * 2;
    return new babylon.Quaternion(
      (m(2, 1) - m(1, 2)) / s,
      (m(0, 2) - m(2, 0)) / s,
      (m(1, 0) - m(0, 1)) / s,
      s / 4,
    );
  }
  if (m(0, 0) > m(1, 1) && m(0, 0) > m(2, 2)) {
    const s = Math.sqrt(1 + m(0, 0) - m(1, 1) - m(2, 2)) * 2;
    return new babylon.Quaternion(
      s / 4,
      (m(0, 1) + m(1, 0)) / s,
      (m(0, 2) + m(2, 0)) / s,
      (m(2, 1) - m(1, 2)) / s,
    );
  }
  if (m(1, 1) > m(2, 2)) {
    const s = Math.sqrt(1 + m(1, 1) - m(0, 0) - m(2, 2)) * 2;
    return new babylon.Quaternion(
      (m(0, 1) + m(1, 0)) / s,
      s / 4,
      (m(1, 2) + m(2, 1)) / s,
      (m(0, 2) - m(2, 0)) / s,
    );
  }
  const s = Math.sqrt(1 + m(2, 2) - m(0, 0) - m(1, 1)) * 2;
  return new babylon.Quaternion(
    (m(0, 2) + m(2, 0)) / s,
    (m(1, 2) + m(2, 1)) / s,
    s / 4,
    (m(1, 0) - m(0, 1)) / s,
  );
}
